import { HisBaseGlobalsService } from './../../../core/service/base/his-base-globals.service';
import { HisDynamicFieldBase } from '../../model/field-base';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { HisDynamicFieldConfig } from '../../model/formfield';
import { HisDynamicFieldConfigAndData } from '../../model/field-config-and-data';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Komponente für dynamische Attribute vom Typ "floatnumber".
 *
 * Zeigt den Wert Locale-abhängig an (deutsch: Komma, englisch: Punkt)
 * und validiert die Eingabe strikt analog zum JSF HisFloatAsStringConverter:
 * - Dezimaltrennzeichen darf nur einmal vorkommen
 * - Tausendergruppen müssen exakt 3 Ziffern haben
 * - Nach dem Dezimaltrennzeichen darf kein Tausendertrennzeichen stehen
 *
 * Bei ungültiger Eingabe wird ein Validierungsfehler angezeigt.
 */
@Component( {
    template: `
        <his-plam [label]="options.label" [helptext]="options.helptext">
            <input #content [hisDdKey]="options.ddKey !== null ? options.ddKey : options.name"
                [formControl]="formControl"
                class="inputWidth100"
                [class.ui-inputtext-invalid]="formControl.invalid && formControl.touched"
                pInputText>
        </his-plam>
        `,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class FloatnumberComponent extends HisDynamicFieldBase<string> implements OnInit {
    options: HisDynamicFieldConfig;
    private hbgs: HisBaseGlobalsService;

    constructor() {
        const injectedData = inject<HisDynamicFieldConfigAndData>( 'injectedData' as any );
        const hbgs = inject( HisBaseGlobalsService );

        super( injectedData.formGroup, injectedData.options, injectedData.valueGroup, hbgs );
        this.options = injectedData.options;
        this.hbgs = hbgs;
    }

    ngOnInit() {
        // Der Wert aus dem Backend liegt in englischer Notation vor (z.B. "3.5").
        // Für die Anzeige in die aktuelle Locale umwandeln.
        let displayValue = this.toLocaleDisplay( this.value );
        super.initFormControl( displayValue );
        // Validator hinzufügen für strikte Locale-abhängige Prüfung
        this.formControl.addValidators( this.floatValidator() );
        this.formControl.updateValueAndValidity( { emitEvent: false } );
    }

    updateConfiguration( entireFormConfig: HisDynamicFieldConfig ) {
        let matchingConfig = this.searchFieldConfig( entireFormConfig );
        if ( matchingConfig !== null ) {
            let displayValue = this.toLocaleDisplay( matchingConfig.defaultValue );
            this.formControl.setValue( displayValue, { emitEvent: false } );
        }
    }

    /**
     * Gibt den Wert für das Backend zurück.
     * Bei gültigem Wert: englische Notation (z.B. "1.75").
     * Bei ungültigem Wert: Rohwert unverändert, damit die Backend-Validierung
     * den Fehler meldet und die Benutzereingabe erhalten bleibt
     */
    getControlValue(): any {
        let raw = this.formControl.value;
        if ( !raw || raw.trim() === '' ) {
            return null;
        }
        let parsed = this.parseValue( raw.trim() );
        if ( parsed !== null ) {
            return parsed;
        } else {
            return raw.trim();
        }
    }

    /**
     * Validator der die Eingabe strikt Locale-abhängig prüft,
     * analog zum JSF HisFloatAsStringConverter.parseLocaleAware().
     * In deutscher Locale werden sowohl deutsche als auch englische Notation akzeptiert.
     */
    private floatValidator(): ValidatorFn {
        return ( control: AbstractControl ): ValidationErrors | null => {
            let value = control.value;
            if ( !value || value.trim() === '' ) {
                return null; // Leere Werte sind okay (ggf. required-Validator greift separat)
            }

            let result = this.parseValue( value.trim() );
            if ( result === null ) {
                return { floatnumber: true };
            }
            return null;
        };
    }

    /**
     * Versucht den Wert zu parsen. In deutscher Locale wird zuerst die
     * deutsche Notation versucht, dann als Fallback die englische Notation.
     * In englischer Locale wird nur englisch geparst.
     *
     * @returns englische Notation als String (z.B. "3.5"), oder null bei ungültigem Wert
     */
    private parseValue( value: string ): string | null {
        let result = this.parseLocaleAware( value );
        if ( result !== null ) {
            return result;
        }
        // Fallback: In deutscher Locale auch englische Notation akzeptieren
        let locale = this.getCurrentLocale();
        if ( locale === 'de' || locale === 'de-DE' ) {
            return this.parseWithLocale( value, '.', ',' );
        }
        return null;
    }

    /**
     * Parst einen Wert strikt Locale-abhängig, analog zum JSF
     * HisFloatAsStringConverter.parseLocaleAware().
     *
     * Regeln:
     * - Dezimaltrennzeichen darf nur einmal vorkommen
     * - Nach dem Dezimaltrennzeichen darf kein Tausendertrennzeichen stehen
     * - Tausendergruppen müssen exakt 3 Ziffern haben (außer der ersten Gruppe)
     *
     * @returns englische Notation als String (z.B. "3.5"), oder null bei ungültigem Wert
     */
    private parseLocaleAware( value: string ): string | null {
        if ( !value ) {
            return null;
        }

        let locale = this.getCurrentLocale();
        let decimalChar: string;
        let groupingChar: string;

        if ( locale === 'de' || locale === 'de-DE' ) {
            decimalChar = ',';
            groupingChar = '.';
        } else {
            decimalChar = '.';
            groupingChar = ',';
        }

        let integerPart = value;
        let decimalPart = '';

        // Dezimaltrennzeichen prüfen
        if ( value.includes( decimalChar ) ) {
            let parts = value.split( decimalChar );
            // Dezimaltrennzeichen darf nur einmal vorkommen
            if ( parts.length > 2 ) {
                return null;
            }
            integerPart = parts[0];
            decimalPart = parts[1];

            // Nach dem Dezimaltrennzeichen darf kein Tausendertrennzeichen stehen
            if ( decimalPart.includes( groupingChar ) ) {
                return null;
            }

            // Nachkommastellen dürfen nur Ziffern enthalten
            if ( !/^\d+$/.test( decimalPart ) ) {
                return null;
            }
        }

        // Vorzeichen behandeln
        let sign = '';
        let intPartToCheck = integerPart;
        if ( intPartToCheck.startsWith( '-' ) || intPartToCheck.startsWith( '+' ) ) {
            sign = intPartToCheck.charAt( 0 );
            intPartToCheck = intPartToCheck.substring( 1 );
        }

        // Ganzzahlteil darf nicht leer sein
        if ( intPartToCheck === '' ) {
            return null;
        }

        // Tausendergruppierung prüfen
        if ( intPartToCheck.includes( groupingChar ) ) {
            let groups = intPartToCheck.split( groupingChar );
            for ( let i = 0; i < groups.length; i++ ) {
                // Jede Gruppe darf nur Ziffern enthalten
                if ( !/^\d+$/.test( groups[i] ) ) {
                    return null;
                }
                if ( i === 0 ) {
                    // Erste Gruppe: 1-3 Ziffern
                    if ( groups[i].length < 1 || groups[i].length > 3 ) {
                        return null;
                    }
                } else {
                    // Folgegruppen: exakt 3 Ziffern
                    if ( groups[i].length !== 3 ) {
                        return null;
                    }
                }
            }
            // Tausendertrennzeichen entfernen
            intPartToCheck = intPartToCheck.replace( new RegExp( '\\' + groupingChar, 'g' ), '' );
        } else {
            // Ohne Tausendertrennzeichen: nur Ziffern erlaubt
            if ( !/^\d+$/.test( intPartToCheck ) ) {
                return null;
            }
        }

        // Englische Notation zusammenbauen
        let result = sign + intPartToCheck;
        if ( decimalPart !== '' ) {
            result += '.' + decimalPart;
        }

        // Abschließend prüfen ob parsbar
        let num = parseFloat( result );
        if ( isNaN( num ) ) {
            return null;
        }

        return result;
    }

    /**
     * Parst einen Wert mit explizit angegebenen Trennzeichen.
     * Wird als Fallback verwendet, um in deutscher Locale auch englische Notation zu akzeptieren.
     *
     * @param value der zu parsende Wert
     * @param decimalChar das Dezimaltrennzeichen
     * @param groupingChar das Tausendertrennzeichen
     * @returns englische Notation als String (z.B. "3.5"), oder null bei ungültigem Wert
     */
    private parseWithLocale( value: string, decimalChar: string, groupingChar: string ): string | null {
        if ( !value ) {
            return null;
        }

        let integerPart = value;
        let decimalPart = '';

        // Dezimaltrennzeichen prüfen
        if ( value.includes( decimalChar ) ) {
            let parts = value.split( decimalChar );
            if ( parts.length > 2 ) {
                return null;
            }
            integerPart = parts[0];
            decimalPart = parts[1];

            if ( decimalPart.includes( groupingChar ) ) {
                return null;
            }

            if ( !/^\d+$/.test( decimalPart ) ) {
                return null;
            }
        }

        // Vorzeichen behandeln
        let sign = '';
        let intPartToCheck = integerPart;
        if ( intPartToCheck.startsWith( '-' ) || intPartToCheck.startsWith( '+' ) ) {
            sign = intPartToCheck.charAt( 0 );
            intPartToCheck = intPartToCheck.substring( 1 );
        }

        if ( intPartToCheck === '' ) {
            return null;
        }

        // Tausendergruppierung prüfen
        if ( intPartToCheck.includes( groupingChar ) ) {
            let groups = intPartToCheck.split( groupingChar );
            for ( let i = 0; i < groups.length; i++ ) {
                if ( !/^\d+$/.test( groups[i] ) ) {
                    return null;
                }
                if ( i === 0 ) {
                    if ( groups[i].length < 1 || groups[i].length > 3 ) {
                        return null;
                    }
                } else {
                    if ( groups[i].length !== 3 ) {
                        return null;
                    }
                }
            }
            intPartToCheck = intPartToCheck.replace( new RegExp( '\\' + groupingChar, 'g' ), '' );
        } else {
            if ( !/^\d+$/.test( intPartToCheck ) ) {
                return null;
            }
        }

        let result = sign + intPartToCheck;
        if ( decimalPart !== '' ) {
            result += '.' + decimalPart;
        }

        let num = parseFloat( result );
        if ( isNaN( num ) ) {
            return null;
        }

        return result;
    }

    /**
     * Konvertiert einen Wert in englischer Notation (z.B. "3.5") in die
     * Locale-abhängige Anzeige (z.B. "3,5" für Deutsch).
     * Wird nur für bekannte gültige Werte aus dem Backend aufgerufen.
     */
    private toLocaleDisplay( value: any ): string {
        if ( value === null || value === undefined || value === '' ) {
            return '';
        }
        let str = String( value ).trim();
        if ( str === '' ) {
            return '';
        }

        let num = parseFloat( str );
        if ( isNaN( num ) ) {
            return str;
        }

        // Locale-abhängige Formatierung
        let locale = this.getCurrentLocale();
        return num.toLocaleString( locale, {
            useGrouping: false,
            minimumFractionDigits: 1,
            maximumFractionDigits: this.getDecimalPlaces( str )
        } );
    }


    private getDecimalPlaces( englishValue: string ): number {
        let dotIndex = englishValue.indexOf( '.' );
        if ( dotIndex < 0 ) {
            return 1;
        }
        return Math.max( 1, englishValue.length - dotIndex - 1 );
    }

    private getCurrentLocale(): string {
        return this.hbgs.getNearestLocaleSupportedByClient();
    }
}
